import * as React from 'react'

import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import { PIN_SPEC } from '~/client/src/shared/map/markerGeometry'

import {
  getProjectPinImageKey,
  renderMissingMarkerImage,
} from '../utils/GLMarkerImageGenerator'

import Colors from '~/client/src/shared/theme.module.scss'

export const PROJECT_PIN_SOURCE_ID = 'project-address-pin-source'
export const PROJECT_PIN_LAYER_ID = 'project-address-pin'

// Not var(--primary): a canvas cannot read a CSS custom property.
const PROJECT_PIN_IMAGE_KEY = getProjectPinImageKey(Colors.brandBlue)

// Hoisted: react-map-gl only diffs layout on identity change, so an inline object costs a deep
// walk of every key per render — 20-40ms per pointer event across the location-marker layers.
const LAYOUT = {
  'icon-image': PROJECT_PIN_IMAGE_KEY,
  // The DOM marker this replaces centres its box on the coordinate, and its draggable twin on
  // Project Settings still does, so the offset keeps both pins in the same place.
  'icon-anchor': 'bottom',
  'icon-offset': [0, PIN_SPEC.boxPx / 2],
  'icon-size': 1,
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
}

interface IProps {
  latitude: number
  longitude: number
}

/**
 * Below PROJECT_MARKER_ZOOM_LEVEL every location marker is dropped and this stands in for all of
 * them, so it must survive a `getCanvas()` capture — a DOM `<Marker>` does not. The draggable twin
 * on Project Settings stays DOM; that screen is never captured.
 */
function GLProjectAddressMarker({ latitude, longitude }: IProps): JSX.Element {
  const { current: map } = useMap()

  React.useEffect(() => {
    const gl = map?.getMap()
    if (gl) renderMissingMarkerImage(gl, PROJECT_PIN_IMAGE_KEY)
  }, [map])

  const data = React.useMemo(
    () =>
      ({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Point', coordinates: [longitude, latitude] },
          },
        ],
      }) as GeoJSON.FeatureCollection,
    [longitude, latitude],
  )

  return (
    <Source id={PROJECT_PIN_SOURCE_ID} type="geojson" data={data as never}>
      <Layer id={PROJECT_PIN_LAYER_ID} type="symbol" layout={LAYOUT as never} />
    </Source>
  )
}

export default React.memo(GLProjectAddressMarker)
